import React from 'react'
import { Alert, Box, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import { presentConnectorErrors, VIEW } from './connectorImportResult'

// Connector texts (message, errors, leftovers) are painted as they arrive: untranslated and untruncated.
const DETAIL_SX = { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }

const Detail = ({ children, testId }) => (
  <Typography variant='body2' sx={DETAIL_SX} data-testid={testId}>{children}</Typography>
)

const ImportedBlock = ({ result, t_ }) => (
  <Alert severity='success' data-testid='connector-result'>
    <Typography fontWeight='bold'>{t_('The file has been imported')}</Typography>
    <Typography variant='body2'>{t_('Main part reference')}: {result.mainPartReference}</Typography>
    <Typography variant='body2'>{t_('Part references')}: {result.partReferences}</Typography>
    <Typography variant='body2'>{t_('Ignored rows')}: {result.ignoredRows}</Typography>
    <Typography variant='body2'>{t_('Fabrication orders')}: {result.fabricationOrders.join(', ')}</Typography>
  </Alert>
)

const ErrorsTable = ({ errors, t_ }) => (
  <Table size='small' data-testid='connector-errors-table'>
    <TableHead>
      <TableRow>
        <TableCell>{t_('Line')}</TableCell>
        <TableCell>{t_('Column')}</TableCell>
        <TableCell>{t_('Reason')}</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {presentConnectorErrors(errors).map((error, index) => (
        <TableRow key={index} data-testid='connector-error-row'>
          <TableCell>{error.lineKey ? t_(error.lineKey) : error.line}</TableCell>
          <TableCell>{error.columnKey ? t_(error.columnKey) : error.column}</TableCell>
          <TableCell sx={DETAIL_SX}>{error.reason}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

const RejectedBlock = ({ result, t_ }) => (
  <Alert severity='error' data-testid='connector-result'>
    <Typography fontWeight='bold'>{t_('The file has not been imported. Nothing has been created')}</Typography>
    {result.errors.length > 0
      ? <ErrorsTable errors={result.errors} t_={t_} />
      : <Detail testId='connector-result-detail'>{result.message}</Detail>}
  </Alert>
)

const UndoneFailureBlock = ({ result, t_ }) => (
  <Alert severity='error' data-testid='connector-result'>
    <Typography fontWeight='bold'>{t_('The import failed and what had been created has been deleted')}</Typography>
    {result.leftovers.length > 0 && (
      <Box>
        <Typography>{t_('These items could not be deleted and must be removed by hand')}</Typography>
        <ul data-testid='connector-leftovers'>
          {result.leftovers.map((leftover, index) => <li key={index} style={DETAIL_SX}>{String(leftover)}</li>)}
        </ul>
      </Box>
    )}
    <Detail testId='connector-result-detail'>{result.message}</Detail>
  </Alert>
)

const HEADLINE_BY_VIEW = {
  [VIEW.CONNECTOR_UNAVAILABLE]: 'The import connector is not available. Nothing has been created. Contact your administrator',
  [VIEW.NOT_CONFIGURED]: 'No import connector is configured',
  [VIEW.FORBIDDEN]: 'You do not have permission to import files',
  [VIEW.UNKNOWN]: 'The result of the import is unknown. Check the fabrication orders and part references before importing the file again'
}

/** Result of a load under the row of the import connector dialog (story 194, CU5): one block per view. */
const ImportConnectorResult = ({ result }) => {
  const { t_ } = useSmartTranslation()
  if (result.view === '') return <></>
  let block
  if (result.view === VIEW.IMPORTED) block = <ImportedBlock result={result} t_={t_} />
  else if (result.view === VIEW.REJECTED) block = <RejectedBlock result={result} t_={t_} />
  else if (result.view === VIEW.UNDONE_FAILURE) block = <UndoneFailureBlock result={result} t_={t_} />
  else {
    block = (
      <Alert severity={result.view === VIEW.UNKNOWN ? 'warning' : 'error'} data-testid='connector-result'>
        <Typography fontWeight='bold'>{t_(HEADLINE_BY_VIEW[result.view])}</Typography>
      </Alert>
    )
  }
  return (
    <Stack sx={{ marginTop: 2 }}>
      {block}
      {/* Marker of the classified view, not shown. */}
      <span hidden data-testid='connector-result-view'>{result.view}</span>
    </Stack>
  )
}

export default ImportConnectorResult
